/**
 * GTM Integration Public API
 *
 * Exports all public functions for GTM integration
 */

// Internal imports for bootstrapGTM orchestration
import { initializeDataLayer } from './dataLayer';
import { captureAttribution } from './attribution';
import { loadGTMContainer } from './gtmLoader';
import { capturePreviewParams, pushPreviewContext } from './sgtmPreview';
import { pushVenueContext } from './dataLayer';
import { restoreSavedConsent } from './consent';
import { warn } from './logger';

let bootstrapped = false;

/**
 * One-shot GTM initialization. Captures URL context, seeds dataLayer,
 * and injects the GTM container script.
 *
 * Consent Mode v2 defaults (all denied) are already set by an inline
 * script in the HTML template — that runs before any JS bundle parses.
 *
 * Call once on app mount — ordering is enforced internally.
 */
export function bootstrapGTM(): void {
  if (bootstrapped) {
    warn('[GTM] bootstrapGTM() already called — skipping duplicate invocation');
    return;
  }
  bootstrapped = true;
  capturePreviewParams(); // 1. Capture sGTM preview params from URL
  initializeDataLayer(); //  2. Ensure window.dataLayer exists
  restoreSavedConsent(); //  3. Restore returning-user consent before GTM reads dataLayer
  captureAttribution(); //   4. Capture UTM / click-ID params to localStorage
  pushPreviewContext(); //   5. Seed sGTM preview fields (if active)
  pushVenueContext(); //     6. Seed venue_slug before GTM reads dataLayer
  loadGTMContainer(); //     7. Push gtm.js event + inject <script>
}

// Core data layer functions
export {
  pushToDataLayer,
  getSessionId,
  generateEventId,
  getConsentState,
  getVenueSlugFromURL,
  emitSessionBootstrap,
  replayBufferedEvents
} from './dataLayer';

// Attribution tracking
export { getAttribution, clearAttribution } from './attribution';

// Consent management
export { updateConsentMode } from './consent';

// GTM script loader
export { isGTMLoaded } from './gtmLoader';

// sGTM debug preview
export { isPreviewActive } from './sgtmPreview';

// Surface layer — import directly from helpers/gtm/surface/events,
// helpers/gtm/surface/types, or helpers/gtm/surface/useGTMAnalytics.
// No barrel re-export to avoid circular dependency with RestaurantGuardContext.

// Type exports
export type {
  ConsentState,
  AttributionData,
  PublicDataLayerEvent,
  TablzNamespace
} from './types';
